import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { useDispatch } from 'react-redux'
import { Box, Divider, Typography, IconButton } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import CloseIcon from '@mui/icons-material/Close'
import debounce from 'lodash/debounce'

import { useMutation, useQuery } from '~/shared/hooks/query'
import { CaseCategory, CaseListItem } from '~/entities/case'
import { queueNotification } from '~/shared/notifications/actions'
import {
  providersCasesGenerateDescription,
  ProvidersCasesGenerateDescriptionRetrieveResponse,
} from '@fireflyhealth/api'
import { required } from '~/shared/lib/required'
import { useFocusOnOpen } from '~/shared/hooks/useFocusOnOpen'

interface ChatThreadDescriptionProps {
  selectedCategory: CaseCategory | null
  focusedCase?: CaseListItem | null
  message: string
}

export interface ChatThreadDescriptionHandle {
  getDescription: () => string | undefined
}

/**
 * In-memory cache for completed description generation jobs
 * Maps "categoryId:message" to the completed job result
 */
const descriptionCache = new Map<string, ProvidersCasesGenerateDescriptionRetrieveResponse>()

/**
 * Generate a cache key from categoryId and message
 */
function getCacheKey(categoryId: number, message: string): string {
  return `${categoryId}:${message.trim()}`
}

/**
 * Creates an async job to generate a description and returns the job ID
 */
async function createGenerationJob(categoryId: number, message: string) {
  const { data } = await providersCasesGenerateDescription.create(
    {
      provider_id: 'me',
    },
    {
      data: {
        category_id: categoryId,
        message: message.trim(),
      },
    },
  )
  return data.id
}

/**
 * Polls the job status endpoint to get the current status and description
 */
async function pollJobStatus(jobId: number) {
  const { data } = await providersCasesGenerateDescription.retrieve({
    provider_id: 'me',
    id: jobId,
  })
  return data
}

/**
 * Component that renders text with a character-by-character fade-in animation
 * (LLM-style streaming effect)
 */
const AnimatedText: React.FC<{ text: string }> = ({ text }) => {
  const [displayedChars, setDisplayedChars] = useState(0)

  useEffect(() => {
    if (displayedChars >= text.length) return

    const timer = setTimeout(() => {
      setDisplayedChars(prev => prev + 1)
    }, 15) // transition per character for smooth animation

    return () => clearTimeout(timer)
  }, [displayedChars, text.length])

  return (
    <span>
      {text.split('').map((char, index) => (
        <span
          key={`animated_text_${index}`}
          style={{
            opacity: index < displayedChars ? 1 : 0,
            transition: 'opacity 0.15s ease-in',
            display: 'inline',
          }}
        >
          {char}
        </span>
      ))}
    </span>
  )
}

export const ChatThreadDescription = forwardRef<
  ChatThreadDescriptionHandle,
  ChatThreadDescriptionProps
>(({ selectedCategory, focusedCase, message }, ref) => {
  const trimmedMessage = message.trim()
  const [debouncedParams, setDebouncedParams] = useState<{
    categoryId: number
    message: string
  } | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [overrideText, setOverrideText] = useState('')
  const textFieldRef = useRef<HTMLInputElement>(null)
  const dispatch = useDispatch()

  // Determine the category ID from either selectedCategory or focusedCase
  const categoryId = selectedCategory?.id ?? focusedCase?.category

  // Create a stable debounced function using useRef
  const debouncedSetParamsRef = useRef(
    debounce((catId: number, msg: string) => {
      setDebouncedParams({ categoryId: catId, message: msg })
    }, 500),
  )

  // Debounce message changes; a change of category cancels the pending call through cleanup.
  useEffect(() => {
    const debouncedSetParams = debouncedSetParamsRef.current
    if (categoryId && trimmedMessage) debouncedSetParams(categoryId, trimmedMessage)
    return () => debouncedSetParams.cancel()
  }, [categoryId, trimmedMessage])

  // Settled params are dropped once the message empties or the category changes.
  if (debouncedParams && (!trimmedMessage || debouncedParams.categoryId !== categoryId)) {
    setDebouncedParams(null)
  }
  const activeParams = debouncedParams

  // An empty override leaves edit mode, including when the category changes under it.
  const [editedCategory, setEditedCategory] = useState(selectedCategory)
  if (selectedCategory !== editedCategory) {
    setEditedCategory(selectedCategory)
    if (!overrideText.trim()) setIsEditing(false)
  }

  const handleEnableOverride = () => setIsEditing(true)
  useFocusOnOpen(textFieldRef, isEditing)

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOverrideText(e.target.value)
    if (!e.target.value.trim()) setIsEditing(false)
  }

  const handleTextFieldBlur = () => {
    if (!overrideText.trim() && !jobStatusQuery.error) {
      setIsEditing(false)
      setOverrideText('')
    }
  }

  const handleClearClick = () => {
    setOverrideText('')
    setIsEditing(false)
    textFieldRef.current?.focus()
  }

  const handleErrorNotification = () => {
    dispatch(
      queueNotification({
        message: 'Failed to create thread title',
        variant: 'error',
      }),
    )
  }

  // Check if we have a cached result for the current params
  const cachedResult = activeParams
    ? descriptionCache.get(getCacheKey(activeParams.categoryId, activeParams.message))
    : null

  // Create job mutation
  const createJobMutation = useMutation(
    (params: { categoryId: number; message: string }) =>
      createGenerationJob(params.categoryId, params.message),
    {
      onError: () => {
        handleEnableOverride()
        handleErrorNotification()
      },
    },
  )
  const { mutate: createJob } = createJobMutation

  const fetchJobStatus = async (job: number) => {
    const data = await pollJobStatus(job)
    if (data.status === 'complete' && activeParams) {
      descriptionCache.set(getCacheKey(activeParams.categoryId, activeParams.message), data)
    }
    if (data.status === 'failed') {
      handleEnableOverride()
      handleErrorNotification()
    }
    return data
  }

  // Poll the job every 500ms until it completes or fails; a finished job's status never changes.
  const jobStatusQuery = useQuery(
    ['jobStatus', createJobMutation.data],
    () => fetchJobStatus(required(createJobMutation.data, 'a summary job')),
    {
      enabled: !!createJobMutation.data && !isEditing && !cachedResult,
      staleTime: Infinity,
      refetchInterval: query =>
        query.state.data?.status === 'complete' || query.state.data?.status === 'failed'
          ? false
          : 500,
    },
  )

  // A poll that still fails after its retries opens the override, once per failure.
  const [failedPoll, setFailedPoll] = useState<Error | null>(null)
  if (jobStatusQuery.error !== failedPoll) {
    setFailedPoll(jobStatusQuery.error)
    if (jobStatusQuery.error) handleEnableOverride()
  }

  // Trigger job creation when the active params change
  useEffect(() => {
    if (!activeParams || isEditing) return
    // If we have a cached result, use it instead of creating a new job
    if (!cachedResult) createJob(activeParams)
  }, [activeParams, isEditing, cachedResult, createJob])

  const generatedDescription = cachedResult?.description || jobStatusQuery.data?.description
  const isGenerating = (createJobMutation.isPending || jobStatusQuery.isFetching) && !cachedResult
  const hasError = createJobMutation.isError || jobStatusQuery.isError

  // Expose getDescription method via ref
  useImperativeHandle(ref, () => ({
    getDescription: () => overrideText || generatedDescription || undefined,
  }))

  // Don't render anything if there's no category or message. The
  // selectedCategory check already happens on the parent, this is
  // for extra safety
  if (!trimmedMessage) {
    return null
  }

  return (
    <Box>
      <Box
        onClick={!isEditing ? handleEnableOverride : undefined}
        sx={({ palette }) => ({
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: isEditing ? palette.common.white : palette.grey[50],
          cursor: !isEditing ? 'pointer' : 'default',
          transition: 'background-color 0.2s',
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          {isEditing ? (
            <input
              ref={textFieldRef}
              type="text"
              value={overrideText}
              onChange={handleTextFieldChange}
              onBlur={handleTextFieldBlur}
              placeholder="Enter custom thread title..."
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '1em',
                fontFamily: 'inherit',
                backgroundColor: 'transparent',
                lineHeight: 1.5,
              }}
            />
          ) : (
            <>
              {!generatedDescription && !hasError && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography variant="caption" sx={{ fontSize: '1em' }}>
                    ✨
                  </Typography>
                  <Skeleton animation="wave" variant="text" width="33%" height={16} />
                </Box>
              )}
              {generatedDescription && !isGenerating && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '1em' }}>
                  ✨ Thread title:{' '}
                  <strong>
                    <AnimatedText text={generatedDescription} />
                  </strong>
                </Typography>
              )}
            </>
          )}
        </Box>
        {/**
         * Only show the clear button if there is text and no error
         * The clear button should not be shown when the description is loading
         * or when there is an error because there is no text to clear in those cases
         */}
        {isEditing && overrideText && !hasError && (
          <IconButton edge="end" size="small" onClick={handleClearClick} sx={{ ml: 1, my: -2 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Divider />
    </Box>
  )
})

ChatThreadDescription.displayName = 'ChatThreadDescription'
